import { useEffect, useRef } from 'react';

import type { PrincipalKind } from '@/auth/types';
import { api } from '@/lib/api';

type InteractionPrincipal = 'user' | 'kid';
type RouteTarget = { path: string; target: string; exact?: boolean };

const PARENT_ROUTE_TARGETS: RouteTarget[] = [
  { path: '/portal/academy/hsc-planner', target: 'hsc' },
  { path: '/portal/challenge', target: 'challenge' },
  { path: '/portal/courses', target: 'courses' },
  { path: '/portal/classes', target: 'courses' },
  { path: '/portal/teachers', target: 'tutoring' },
  { path: '/portal/academy', target: 'academy' },
  { path: '/portal/tutoring', target: 'tutoring' },
  { path: '/portal/guides', target: 'guides' },
  { path: '/portal/family', target: 'family' },
  { path: '/portal/approvals', target: 'approvals' },
  { path: '/portal/usage', target: 'usage' },
  { path: '/portal/audit', target: 'activity' },
  { path: '/portal/wallet', target: 'wallet' },
  { path: '/portal/billing', target: 'billing' },
  { path: '/portal/settings', target: 'settings' },
  { path: '/portal', target: 'dashboard', exact: true },
];

const KID_ROUTE_TARGETS: RouteTarget[] = [
  { path: '/learn/create/blocks', target: 'story_blocks' },
  { path: '/learn/blocks', target: 'story_blocks' },
  { path: '/learn/playground', target: 'creative_code' },
  { path: '/learn/create/playground', target: 'creative_code' },
  { path: '/learn/create/image', target: 'image_studio' },
  { path: '/learn/music', target: 'music_studio' },
  { path: '/learn/create/music', target: 'music_studio' },
  { path: '/learn/code', target: 'code_studio' },
  { path: '/learn/create/code', target: 'code_studio' },
  { path: '/learn/create', target: 'create' },
  { path: '/learn/projects', target: 'projects' },
  { path: '/learn/classroom', target: 'classroom' },
  { path: '/learn/passport', target: 'passport' },
  { path: '/learn/workspace', target: 'ai_studio' },
  { path: '/learn/missions', target: 'lessons' },
  { path: '/learn/hsc', target: 'hsc_plan' },
  { path: '/learn/profile', target: 'profile' },
  { path: '/learn', target: 'home', exact: true },
];

function targetForPath(pathname: string, routes: RouteTarget[]): string | null {
  const match = routes.find(({ path, exact }) =>
    exact
      ? pathname === path || pathname === `${path}/`
      : pathname === path || pathname.startsWith(`${path}/`),
  );
  return match?.target ?? null;
}

export function productInteractionTarget(
  principal: InteractionPrincipal,
  pathname: string,
): string | null {
  return targetForPath(pathname, principal === 'user' ? PARENT_ROUTE_TARGETS : KID_ROUTE_TARGETS);
}

/**
 * Records one first-party product-surface open per mounted app shell.
 * The backend derives the surface and identity from the JWT; no route, query,
 * email, nickname or other client data is sent.
 */
export function useProductOpenTracking(principal: PrincipalKind) {
  const reported = useRef(false);
  const sessionNonce = useRef<string>();
  if (!sessionNonce.current) {
    sessionNonce.current = crypto.randomUUID();
  }

  useEffect(() => {
    if (reported.current) return;
    reported.current = true;

    void api('/analytics/open', {
      method: 'POST',
      principal,
      body: { session_nonce: sessionNonce.current },
    }).catch(() => {
      // A transient failure may retry if the shell remounts; telemetry must never
      // interrupt or visibly degrade the Parent or Kids experience.
      reported.current = false;
    });
  }, [principal]);
}

/** Captures only same-origin, allowlisted navigation clicks; no URL or visible text is sent. */
export function useProductInteractionTracking(principal: InteractionPrincipal) {
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      const element = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!(element instanceof HTMLAnchorElement)) return;
      const url = new URL(element.href, window.location.origin);
      if (url.origin !== window.location.origin) return;
      const target = productInteractionTarget(principal, url.pathname);
      if (!target) return;
      void api('/analytics/interaction', {
        method: 'POST',
        principal,
        body: { target },
      }).catch(() => undefined);
    };

    // Capture before React Router handles the link and calls preventDefault()
    // for client-side navigation. A bubbling listener would otherwise discard
    // every normal SPA navigation as an already-cancelled click.
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [principal]);
}
